import useUserStore from "../states/user";

const DEFAULT_STORE_API_URL = "http://localhost:8082/store";

function normalizeBaseUrl(url) {
	return String(url || "").trim().replace(/\/$/, "");
}

function getStoreApiBaseUrl() {
	const configured = normalizeBaseUrl(process.env.REACT_APP_STORE_API_URL || "");

	if (configured) {
		if (/^:\d+(\/.*)?$/.test(configured)) {
			return `http://localhost${configured}`;
		}

		if (/^localhost:\d+(\/.*)?$/i.test(configured)) {
			return `http://${configured}`;
		}

		return configured;
	}

	if (typeof window !== "undefined" && window.location?.hostname) {
		const protocol = window.location.protocol || "http:";
		const hostname = window.location.hostname;
		return `${protocol}//${hostname}:8082/store`;
	}

	return DEFAULT_STORE_API_URL;
}

const STORE_API_BASE_URL = getStoreApiBaseUrl();

function buildStoreApiUrl(path) {
	const normalizedPath = String(path || "").startsWith("/") ? path : `/${path}`;
	return `${STORE_API_BASE_URL}${normalizedPath}`;
}

function fetchWithOrganization(path, options = {}) {
	const organizationId = useUserStore.getState().session?.organization_id;
	if (!organizationId) {
		throw new Error("No organization found for this request. Please sign in again.");
	}

	const url = new URL(buildStoreApiUrl(path));
	const requestOptions = {
		credentials: "include",
		...options,
	};
	const method = (options.method || "GET").toUpperCase();
	if (method === "GET" || method === "HEAD") {
		url.searchParams.set("organization_id", organizationId);
	} else if (options.body instanceof FormData) {
		options.body.set("organization_id", String(organizationId));
	} else {
		requestOptions.headers = { ...options.headers, "Content-Type": "application/json" };
		requestOptions.body = JSON.stringify({
			...(options.body ? JSON.parse(options.body) : {}),
			organization_id: organizationId,
		});
	}
	return fetch(url.toString(), requestOptions);
}

export async function requestJson(path, options = {}) {
	const response = await fetchWithOrganization(path, options);

	let payload = {};
	try {
		payload = await response.json();
	} catch {
		payload = {};
	}

	return { response, payload };
}

export async function requestBlob(path, fallbackMessage) {
	const response = await fetchWithOrganization(path);
	if (!response.ok) {
		let payload = {};
		try {
			payload = await response.json();
		} catch {
			payload = {};
		}
		throw new Error(payload.error || fallbackMessage);
	}
	return response.blob();
}

export function assertOk(response, payload, fallbackMessage) {
	if (!response.ok) {
		const message = payload?.error || fallbackMessage;
		const detail = response.status ? `${message} (${response.status})` : message;
		throw new Error(detail);
	}

	return payload;
}

export async function addToCart(items) {
	const { response, payload } = await requestJson("/cart", {
		method: "POST",
		headers: { "Content-Type": "application/json" },
		body: JSON.stringify({ items }),
	});

	return assertOk(response, payload, "Failed to update cart");
}
